import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { RotateCcw, Home } from "lucide-react";
import "@/App.css";
import { load, save, remove, validateWords, pickPair, deal, winner, pick, loadBoard, emptyBoard, scoreGame } from "./game";
import { Modal, playSound, vibrate } from "./ui";
import Menu from "./screens/Menu";
import Words from "./screens/Words";
import Reveal from "./screens/Reveal";
import Round from "./screens/Round";
import VoteScreen from "./screens/VoteScreen";
import Result from "./screens/Result";
import End from "./screens/End";

const DEFAULTS = { players: [], impostores: 1, categorias: null, modo: "parecida", timerOn: false, timerSecs: 30, sound: true, theme: "dark" };
const IN_GAME = ["reveal", "round", "vote", "result"];

function useWakeLock(active) {
  const lock = useRef(null);
  useEffect(() => {
    if (!active || !("wakeLock" in navigator)) return;
    const req = () => navigator.wakeLock.request("screen").then((l) => (lock.current = l)).catch(() => {});
    const onVis = () => document.visibilityState === "visible" && req();
    req();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      lock.current && lock.current.release().catch(() => {});
      lock.current = null;
    };
  }, [active]);
}

export default function App() {
  const [settings, setSettings] = useState(() => ({ ...DEFAULTS, ...load("settings", {}) }));
  const [words, setWords] = useState(null);
  const [custom, setCustom] = useState(false);
  const [screen, setScreen] = useState("menu");
  const [game, setGame] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [board, setBoard] = useState(loadBoard);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const resetBoard = () => ask("¿Reiniciar el marcador de la noche? Se borrarán todos los puntos.", "Reiniciar", () => { const b = emptyBoard(); setBoard(b); save("board", b); });

  const set = (patch) => setSettings((s) => ({ ...s, ...patch }));
  const play = (k) => playSound(k, settings.sound);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    const syncStandalone = () => setIsStandalone(mediaQuery.matches || window.navigator.standalone === true);

    syncStandalone();
    if (mediaQuery.addEventListener) mediaQuery.addEventListener("change", syncStandalone);
    else mediaQuery.addListener(syncStandalone);

    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };
    const onAppInstalled = () => setInstallPrompt(null);

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      if (mediaQuery.removeEventListener) mediaQuery.removeEventListener("change", syncStandalone);
      else mediaQuery.removeListener(syncStandalone);
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const installApp = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === "accepted") {
      play("tap");
    }
    setInstallPrompt(null);
  };

  useEffect(() => save("settings", settings), [settings]);
  useEffect(() => { document.documentElement.dataset.theme = settings.theme; }, [settings.theme]);
  useWakeLock(screen !== "menu" && screen !== "words");

  const loadOriginal = () => fetch("/palabras.json").then((r) => r.json()).then((d) => { setWords(d); setCustom(false); });
  useEffect(() => {
    const c = load("words", null);
    if (c && !validateWords(c)) { setWords(c); setCustom(true); } else loadOriginal();
  }, []);

  const allCats = words ? words.categorias.map((c) => c.nombre) : [];
  const cats = settings.categorias ? settings.categorias.filter((c) => allCats.includes(c)) : allCats;

  const start = () => {
    const used = load("used", []);
    const { pair, reset } = pickPair(words, cats, used);
    save("used", reset ? [pair.key] : [...used, pair.key]);
    const names = settings.players;
    setGame({ players: deal(names, settings.impostores, pair, settings.modo), pair, mode: settings.modo, round: 1, starter: pick(names), revealIdx: 0, out: null, winner: null, reason: null });
    setScreen("reveal");
    play("tap");
  };

  const finish = (w, reason) => {
    const { board: nb, delta } = scoreGame(board, game.players, w);
    setBoard(nb);
    save("board", nb);
    setGame((g) => ({ ...g, winner: w, reason, delta }));
    setScreen("end");
    play(w === "inocentes" ? "win" : "lose");
    vibrate(w === "inocentes" ? [50, 50, 50] : [300, 100, 300]);
  };

  const nextReveal = () => {
    if (game.revealIdx < game.players.length - 1) setGame((g) => ({ ...g, revealIdx: g.revealIdx + 1 }));
    else setScreen("round");
  };

  const vote = (name) => {
    setGame((g) => ({ ...g, players: g.players.map((p) => (p.name === name ? { ...p, alive: false } : p)), out: name ? g.players.find((p) => p.name === name) : null }));
    setScreen("result");
    if (name) { vibrate([80, 40, 200]); play("eliminate"); }
  };

  const resolve = () => {
    const w = winner(game.players);
    if (w) return finish(w, w === "inocentes" ? "eliminados" : "sin_inocentes");
    const alive = game.players.filter((p) => p.alive).map((p) => p.name);
    setGame((g) => ({ ...g, round: g.round + 1, starter: pick(alive), out: null }));
    setScreen("round");
  };

  const guess = (ok) => (ok ? finish("impostores", "adivino") : resolve());

  const importWords = (d) => { save("words", d); setWords(d); setCustom(true); set({ categorias: null }); };
  const restoreWords = () => { remove("words"); set({ categorias: null }); loadOriginal(); };

  const ask = (text, confirmLabel, action) => setConfirm({ text, confirmLabel, action });

  const views = {
    menu: <Menu settings={settings} set={set} words={words} cats={cats} onStart={start} onWords={() => setScreen("words")} board={board} onResetBoard={resetBoard} />,
    words: <Words words={words} custom={custom} onImport={importWords} onRestore={restoreWords} onBack={() => setScreen("menu")} />,
    reveal: game && <Reveal game={game} onNext={nextReveal} play={play} />,
    round: game && <Round game={game} settings={settings} onVote={() => setScreen("vote")} play={play} />,
    vote: game && <VoteScreen game={game} onVote={vote} play={play} />,
    result: game && <Result game={game} onContinue={resolve} onGuess={guess} play={play} />,
    end: game && <End game={game} board={board} onNew={start} onMenu={() => setScreen("menu")} />,
  };
  const viewKey = `${screen}-${screen === "reveal" ? game?.revealIdx : ""}-${screen === "round" ? game?.round : ""}`;

  return (
    <div className="app">
      {!isStandalone && installPrompt && (
        <div className="pwa-install-banner glass">
          <div className="pwa-install-copy">
            <strong>Instalar la app</strong>
            <p>Guárdala en tu inicio y juega más rápido como PWA.</p>
          </div>
          <div className="pwa-install-actions">
            <button className="btn btn-primary" onClick={installApp}>Instalar</button>
            <button className="btn btn-ghost" onClick={() => setInstallPrompt(null)}>Ahora no</button>
          </div>
        </div>
      )}
      {IN_GAME.includes(screen) && (
        <div className="game-bar">
          <button className="icon-btn" onClick={() => ask("¿Salir al menú? Se perderá la partida actual.", "Salir", () => setScreen("menu"))} aria-label="Menú" data-testid="game-menu-btn"><Home size={20} /></button>
          <span className="game-bar-title">El Impostor</span>
          <button className="icon-btn" onClick={() => ask("¿Reiniciar la partida? Se repartirán nuevos roles y una nueva palabra.", "Reiniciar", start)} aria-label="Reiniciar partida" data-testid="restart-game-btn"><RotateCcw size={20} /></button>
        </div>
      )}
      <AnimatePresence mode="wait">
        <motion.main key={viewKey} className="view" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.28, ease: "easeOut" }}>
          {views[screen]}
        </motion.main>
      </AnimatePresence>
      {confirm && (
        <Modal text={confirm.text} confirmLabel={confirm.confirmLabel} onCancel={() => setConfirm(null)} onConfirm={() => { confirm.action(); setConfirm(null); }} />
      )}

      <footer className="footer-signature" role="contentinfo">
        <span className="footer-signature__text">Hecho con</span>
        <span className="footer-signature__heart" aria-hidden="true">❤️</span>
        <span className="footer-signature__text">por</span>
        <a className="footer-signature__name" href="https://jpfranco.com" target="_blank" rel="noopener noreferrer">Juanpfrancos</a>
        <a className="footer-signature__linkedin" href="https://www.linkedin.com/in/juanpfrancos" target="_blank" rel="noopener noreferrer" aria-label="Perfil de LinkedIn de Juan Pablo">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
          </svg>
        </a>
      </footer>
    </div>
  );
}
